import io from 'socket.io-client';

/**
 * Service WebSocket GPS pour le Frontend Admin
 * Gère la réception des données GPS mobiles en temps réel
 */
import { WS_URL } from '../config/api.config.js';
import logger from '@/utils/logger';
import { getToken, getSchoolId } from '@/utils/storage';

class GPSWebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.connectionStatus = 'disconnected';
    this.baseURL = WS_URL;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.listeners = new Map();

    logger.debug('🔌 GPS WebSocket Service: Initialisation', this.baseURL);
  }

  /**
   * Connecte au WebSocket du backend
   */
  connect(schoolId = null) {
    if (this.socket?.connected) {
      logger.debug('⚠️ GPS WebSocket: Déjà connecté');
      // Si on est déjà connecté mais qu'on change d'école, on devrait peut-être rejoindre la nouvelle room
      if (schoolId && this.currentSchoolId !== schoolId) {
        logger.debug(`🔄 GPS WebSocket: Changement d'école détecté (${this.currentSchoolId} -> ${schoolId})`);
        this.joinSchoolRoom(schoolId);
        this.currentSchoolId = schoolId;
      }
      return;
    }

    this.currentSchoolId = schoolId;
    logger.debug(
      '🔌 GPS WebSocket: Tentative de connexion...',
      schoolId ? `pour l'école ${schoolId}` : '(école par défaut)',
    );

    this.socket = io(this.baseURL, {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 2000,
    });

    this.setupEventListeners();
  }

  /**
   * Configure les écouteurs d'événements WebSocket
   */
  setupEventListeners() {
    if (!this.socket) return;

    // Connexion établie
    this.socket.on('connect', () => {
      // Vérification de sécurité pour éviter l'erreur "Cannot read properties of null (reading 'id')"
      if (!this.socket) {
        logger.warn("⚠️ GPS WebSocket: Socket null lors de l'événement connect");
        return;
      }

      logger.debug('✅ GPS WebSocket: Connexion établie', {
        timestamp: new Date().toISOString(),
        socketId: this.socket.id,
        transport: this.socket.io?.engine?.transport?.name || 'unknown',
        baseURL: this.baseURL,
      });
      this.isConnected = true;
      this.connectionStatus = 'connected';
      this.reconnectAttempts = 0;
      this.emitToListeners('connection_status', { connected: true });

      // Authentifier le client admin — envoyer uniquement si c'est un JWT (3 parties, commence par eyJ)
      const token = getToken();
      const isJwt = token && token.startsWith('eyJ') && token.split('.').length === 3;
      if (isJwt) {
        this.socket.emit('authenticate', { token, type: 'admin' });
      }
      // Ne jamais supprimer le token ici — l'auth WebSocket est indépendante de l'auth HTTP

      // Rejoindre la room de l'école pour recevoir tous les événements GPS
      // TODO: Récupérer le schoolId depuis le contexte utilisateur
      // Pour l'instant, utiliser un ID par défaut
      logger.debug('📡 GPS WebSocket: Rejoindre rooms GPS...');

      // Rejoindre la room globale de l'école (si schoolId disponible)
      // Priorité : paramètre passé à connect() > localStorage > default-school
      const schoolId = this.currentSchoolId || getSchoolId() || 'default-school';
      this.socket.emit('join_room', { room: `school:${schoolId}` });
      logger.debug(`🏫 GPS WebSocket: Room school:${schoolId} rejointe`);

      // Ne pas rejoindre gps_all : cette room reçoit le flux de toutes les écoles (IoT Basic + Premium).
      // L’admin doit uniquement écouter school:{schoolId}, aligné sur GET .../bus-positions-public.

      // Demander les données initiales
      logger.debug('📡 GPS WebSocket: Demande des données initiales...');
      this.requestCurrentGPSData();
      this.requestGPSStats();
    });

    // Connexion fermée
    this.socket.on('disconnect', (reason) => {
      logger.debug('❌ GPS WebSocket: Connexion fermée', reason);
      this.isConnected = false;
      this.connectionStatus = 'disconnected';
      this.emitToListeners('connection_status', { connected: false, reason });
    });

    // Token WebSocket rejeté — log seulement, ne pas vider le token HTTP
    // (l'auth WebSocket et l'auth HTTP REST sont indépendantes)
    this.socket.on('authentication_error', () => {
      logger.warn('⚠️ GPS WebSocket: authentication_error — le token JWT ne passe pas la vérification WebSocket');
    });

    // Erreur de connexion
    this.socket.on('connect_error', (error) => {
      logger.error('❌ GPS WebSocket: Erreur connexion', error);
      this.isConnected = false;
      this.connectionStatus = 'error';
      this.reconnectAttempts++;
      this.emitToListeners('connection_error', { error, attempts: this.reconnectAttempts });
    });

    // Données GPS reçues
    this.socket.on('gps_update', (data) => {
      logger.debug('📍 GPS WebSocket: Données GPS reçues', {
        timestamp: new Date().toISOString(),
        driverId: data.driverId,
        busId: data.busId,
        tripId: data.tripId,
        latitude: data.latitude,
        longitude: data.longitude,
        speed: data.speed,
        heading: data.heading,
        accuracy: data.accuracy,
        source: data.source,
        fullData: data,
      });
      this.emitToListeners('gps_update', data);
    });

    // Changement de statut chauffeur
    this.socket.on('driver_status_change', (data) => {
      logger.debug('👨‍💼 GPS WebSocket: Changement statut chauffeur', {
        timestamp: new Date().toISOString(),
        driverId: data.driverId,
        status: data.status,
        previousStatus: data.previousStatus,
        fullData: data,
      });
      this.emitToListeners('driver_status_change', data);
    });

    // 🚀 Démarrage de trajet
    this.socket.on('trip_started', (data) => {
      logger.debug('🚀 GPS WebSocket: TRAJET DÉMARRÉ', {
        timestamp: new Date().toISOString(),
        tripId: data.tripId,
        driverId: data.driverId,
        busId: data.busId,
        routeId: data.routeId,
        routeName: data.routeName,
        startTime: data.startTime,
        fullData: data,
      });
      this.emitToListeners('trip_started', data);
    });

    // 🏁 Fin de trajet
    this.socket.on('trip_ended', (data) => {
      logger.debug('🏁 GPS WebSocket: TRAJET TERMINÉ', {
        timestamp: new Date().toISOString(),
        tripId: data.tripId,
        driverId: data.driverId,
        busId: data.busId,
        endTime: data.endTime,
        duration: data.duration,
        fullData: data,
      });
      this.emitToListeners('trip_ended', data);
    });

    // Position idle hors trajet (fleet monitor)
    this.socket.on('bus_idle_update', (data) => {
      logger.debug('📍 GPS WebSocket: Position idle reçue', { busId: data.busId, speed: data.speed });
      this.emitToListeners('bus_idle_update', data);
    });

    // Statistiques GPS
    this.socket.on('gps_stats_update', (data) => {
      logger.debug('📊 GPS WebSocket: Statistiques GPS', data);
      this.emitToListeners('gps_stats_update', data);
    });

    // Reconnexion
    this.socket.on('reconnect', (attemptNumber) => {
      logger.debug('🔄 GPS WebSocket: Reconnexion réussie', attemptNumber);
      this.isConnected = true;
      this.connectionStatus = 'connected';
      this.emitToListeners('reconnected', { attempts: attemptNumber });
    });

    // Échec de reconnexion
    this.socket.on('reconnect_failed', () => {
      logger.error('❌ GPS WebSocket: Échec reconnexion définitif');
      this.isConnected = false;
      this.connectionStatus = 'failed';
      this.emitToListeners('reconnect_failed', {});
    });
  }

  /**
   * Émet un événement à tous les listeners
   */
  emitToListeners(eventName, data) {
    if (this.listeners.has(eventName)) {
      this.listeners.get(eventName).forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          logger.error(`❌ GPS WebSocket: Erreur callback ${eventName}`, error);
        }
      });
    }
  }

  /**
   * Ajoute un listener pour un événement
   */
  on(eventName, callback) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }
    this.listeners.get(eventName).add(callback);

    logger.debug(`👂 GPS WebSocket: Listener ajouté pour ${eventName}`);
  }

  /**
   * Supprime un listener
   */
  off(eventName, callback) {
    if (this.listeners.has(eventName)) {
      this.listeners.get(eventName).delete(callback);

      // Supprime la Set si elle est vide
      if (this.listeners.get(eventName).size === 0) {
        this.listeners.delete(eventName);
      }
    }

    logger.debug(`🔇 GPS WebSocket: Listener supprimé pour ${eventName}`);
  }

  /**
   * S'abonne aux mises à jour GPS
   */
  subscribeToGPSUpdates(callback) {
    this.on('gps_update', callback);

    // Demande les données initiales
    if (this.isConnected) {
      this.socket.emit('request_gps_data');
    }
  }

  /**
   * S'abonne aux changements de statut des chauffeurs
   */
  subscribeToDriverStatus(callback) {
    this.on('driver_status_change', callback);
  }

  /**
   * S'abonne aux démarrages de trajet
   */
  subscribeToTripStarted(callback) {
    this.on('trip_started', callback);
    logger.debug('👂 GPS WebSocket: Abonné aux démarrages de trajet');
  }

  /**
   * S'abonne aux fins de trajet
   */
  subscribeToTripEnded(callback) {
    this.on('trip_ended', callback);
    logger.debug('👂 GPS WebSocket: Abonné aux fins de trajet');
  }

  /**
   * S'abonne aux statistiques GPS
   */
  subscribeToGPSStats(callback) {
    this.on('gps_stats_update', callback);

    // Demande les stats initiales
    if (this.isConnected) {
      this.socket.emit('request_gps_stats');
    }
  }

  /**
   * S'abonne aux événements de connexion
   */
  subscribeToConnectionStatus(callback) {
    this.on('connection_status', callback);
    this.on('connection_error', callback);
    this.on('reconnected', callback);
    this.on('reconnect_failed', callback);
  }

  /**
   * Rejoindre une room pour recevoir les mises à jour GPS
   */
  joinRoom(roomType, roomId) {
    if (this.isConnected && this.socket) {
      const room = `${roomType}:${roomId}`;
      logger.debug(`📍 GPS WebSocket: Rejoindre room ${room}`);
      this.socket.emit('join_room', { room });
    } else {
      logger.warn('⚠️ GPS WebSocket: Impossible de rejoindre room - Non connecté');
    }
  }

  /**
   * Rejoindre la room de l'école pour recevoir tous les GPS
   */
  joinSchoolRoom(schoolId) {
    this.joinRoom('school', schoolId);
  }

  /**
   * Rejoindre la room d'un circuit spécifique
   */
  joinRouteRoom(routeId) {
    this.joinRoom('route', routeId);
  }

  /**
   * Rejoindre la room d'un trajet spécifique
   */
  joinTripRoom(tripId) {
    this.joinRoom('trip', tripId);
  }

  /**
   * Rejoindre la room d'un bus spécifique
   */
  joinBusRoom(busId) {
    this.joinRoom('bus', busId);
  }

  /**
   * Demande les données GPS actuelles
   */
  requestCurrentGPSData() {
    if (this.isConnected && this.socket) {
      logger.debug('📡 GPS WebSocket: Demande données GPS actuelles');
      this.socket.emit('request_gps_data');
    } else {
      logger.warn('⚠️ GPS WebSocket: Impossible de demander les données - Non connecté');
    }
  }

  /**
   * Demande les statistiques GPS actuelles
   */
  requestGPSStats() {
    if (this.isConnected && this.socket) {
      logger.debug('📊 GPS WebSocket: Demande statistiques GPS');
      this.socket.emit('request_gps_stats');
    } else {
      logger.warn('⚠️ GPS WebSocket: Impossible de demander les stats - Non connecté');
    }
  }

  /**
   * Filtre les données GPS par source
   */
  subscribeToGPSBySource(source, callback) {
    this.on('gps_update', (data) => {
      if (data.source === source || source === 'all') {
        callback(data);
      }
    });
  }

  /**
   * Obtient le statut de connexion
   */
  getConnectionStatus() {
    return this.connectionStatus;
  }

  /**
   * Déconnecte le WebSocket
   */
  disconnect() {
    if (this.socket) {
      logger.debug('🔌 GPS WebSocket: Déconnexion...');
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;
    this.connectionStatus = 'disconnected';
    this.listeners.clear();
  }

  /**
   * Reconnecte manuellement
   */
  reconnect() {
    logger.debug('🔄 GPS WebSocket: Reconnexion manuelle...');
    this.disconnect();
    setTimeout(() => {
      this.connect();
    }, 1000);
  }

  /**
   * Teste la connexion HTTP au backend
   */
  async testHTTPConnection() {
    try {
      logger.debug('🔍 GPS WebSocket: Test connexion HTTP...');

      const response = await fetch(`${this.baseURL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        logger.debug('✅ GPS WebSocket: Connexion HTTP OK', data);
        return { success: true, data };
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      logger.error('❌ GPS WebSocket: Erreur connexion HTTP', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Obtient les données GPS via HTTP (fallback)
   */
  async getGPSDataHTTP() {
    try {
      const token = getToken();
      const response = await fetch(`${this.baseURL}/api/gps/tracking/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (response.ok) {
        const data = await response.json();
        logger.debug('📊 GPS WebSocket: Données GPS HTTP récupérées', data);
        return data;
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      logger.error('❌ GPS WebSocket: Erreur récupération GPS HTTP', error);
      return null;
    }
  }

  /**
   * Obtient les positions actives via HTTP
   */
  async getActivePositionsHTTP() {
    try {
      const token = getToken();
      const response = await fetch(`${this.baseURL}/api/gps/tracking/positions/active`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (response.ok) {
        const data = await response.json();
        logger.debug('📍 GPS WebSocket: Positions actives HTTP récupérées', data);
        return data;
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      logger.error('❌ GPS WebSocket: Erreur récupération positions HTTP', error);
      return null;
    }
  }
}

// Instance singleton
const gpsWebSocketService = new GPSWebSocketService();

export default gpsWebSocketService;
